import { Component, EventEmitter, OnInit, ViewChild } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { MatTableDataSource } from '@angular/material/table';
import { separator } from '../../../../../constants';
import { SortOrder } from '../../../../../core/api/api';
import { ProductRecentAction, Purchase, ShoppingSessionReference } from '../../../../../core/api/api.entities';
import { PurchasesApi } from '../../../../../core/api/purchases/purchases-api.service';
import { PaginatedPurchaseQuery } from '../../../../../core/api/purchases/queries/purchase.query';
import { DashboardService } from '../../../../../core/services/dashboard.service';
import { DashboardProductForm } from '../../../../../forms/forms/dashboard-product.form';
import { ShoppingItemForm } from '../../../../../forms/forms/shopping-item.form';
import { ShoppingSessionForm } from '../../../../../forms/forms/shopping-session.form';
import { links } from '../../../../../shared/constants/links.constants';
import { PaginatedResult } from '../../../../../shared/models/paginated-result.model';
import { roundNumber } from '../../../../../shared/utils/common.utils';
import { DashboardActionDialogComponent } from '../dashboard-action-dialog/dashboard-action-dialog.component';

@Component({
  selector: 'app-dashboard-register-shopping-dialog',
  templateUrl: './dashboard-register-shopping-dialog.component.html',
  styleUrl: './dashboard-register-shopping-dialog.component.scss',
})
export class DashboardRegisterShoppingDialogComponent extends DashboardActionDialogComponent implements OnInit {
  @ViewChild('stepper') stepper!: MatStepper;

  columns = ['product', 'breakdown', 'price', 'undo'];
  dataSource = new MatTableDataSource<Purchase>([]);
  links = links;
  separator = separator;
  showErrors = false;
  toggleErrors = new EventEmitter<void>();

  query: PaginatedPurchaseQuery = {
    pageSize: 1000,
    sort: [{ column: 'product.productCategory.name', order: SortOrder.asc }],
  };

  constructor(
    private purchasesApi: PurchasesApi,
    service: DashboardService
  ) {
    super(service);
  }

  ngOnInit(): void {
    this.sessionForm.controls.date.setValue(new Date());
    this.forms.product = new DashboardProductForm();
    this.service.product = undefined;

    if (this.sessionForm.existingShoppingSession) {
      this.setDataSourceData();
    }
  }

  get isSubmitting(): boolean {
    return this.service.state.isSubmitting;
  }

  get itemForm(): ShoppingItemForm {
    return this.forms.shopping.item;
  }

  get priceProgress(): number {
    if (this.dataSource) {
      return roundNumber(
        this.dataSource.data.reduce(
          (total, purchase) => total + purchase.amount * ((purchase.price || 0) + (purchase.pant || 0)),
          0
        )
      );
    }

    return 0;
  }

  get progress(): number {
    if (!this.shoppingSession) return 0;
    return (this.priceProgress * 100) / (this.shoppingSession.total - this.shoppingSession.excluded);
  }

  get recentActions(): ProductRecentAction[] {
    return this.service.product?.recentActions || [];
  }

  get sessionForm(): ShoppingSessionForm {
    return this.forms.shopping.session;
  }

  get sessionIsValid(): boolean {
    if (this.sessionForm.useExisting) return !!this.shoppingSession;
    return this.sessionForm.isValid;
  }

  get shoppingSession(): ShoppingSessionReference | null {
    return this.sessionForm.existingShoppingSession;
  }

  createShoppingSession(): void {
    this.service.createShoppingSession().subscribe(() => {
      this.stepper.next();
    });
  }

  registerPurchase(): void {
    this.service.registerPurchase().subscribe((recentPurchases: Purchase[]) => {
      this.dataSource.data = recentPurchases.sort((a, b) =>
        `${a.productCategoryName}${separator}${a.productName}`.localeCompare(
          `${b.productCategoryName}${separator}${b.productName}`
        )
      );
    });
  }

  selectLatestShoppingSession(): void {
    this.sessionForm.controls.existingShoppingSession.setValue(this.sessionForm.options.shoppingSessions[0]);

    setTimeout(() => {
      this.selectedShoppingSession();
    }, 0);
  }

  selectedShoppingSession(): void {
    if (!this.shoppingSession) {
      this.dataSource.data = [];
      return;
    }

    this.setDataSourceData();
  }

  undoPurchase(purchaseId: number): void {
    this.service.undoPurchase(purchaseId).subscribe((undone: boolean) => {
      if (undone) {
        this.dataSource.data = this.dataSource.data.filter(purchase => purchase.id !== purchaseId);
      }
    });
  }

  private setDataSourceData(): void {
    const query = { ...this.query, shoppingSessionId: this.shoppingSession?.id };

    this.purchasesApi.findAll(query).subscribe((result: PaginatedResult<Purchase>) => {
      this.dataSource.data = result.data.sort((a, b) =>
        `${a.productCategoryName}${separator}${a.productName}`.localeCompare(
          `${b.productCategoryName}${separator}${b.productName}`
        )
      );

      this.stepper.next();
    });
  }
}
